import { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Checkbox } from '../components/ui/checkbox';
import { Input } from '../components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { 
  History, 
  Loader2, 
  ChevronRight, 
  Calendar,
  Search,
  Download,
  ArrowLeft,
  CheckCircle2,
  XCircle,
  Clock,
  FileText,
  Trash2,
  FileSpreadsheet,
  ArrowUp,
  ArrowDown,
  X
} from 'lucide-react';
import { toast } from 'sonner';
import WorldGlobe from '../components/ui/WorldGlobe';

const API_URL = `${process.env.REACT_APP_API_URL || 'http://localhost:8001'}/api`;

const Reports = () => {
  const { token } = useAuth();
  const navigate = useNavigate();
  const [searches, setSearches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedSearch, setSelectedSearch] = useState(null);
  
  // Multi-select state
  const [selectedSearchIds, setSelectedSearchIds] = useState(new Set());
  const [exporting, setExporting] = useState(false);

  // P4.2: filter + sort state
  const [searchQuery, setSearchQuery] = useState('');
  // P4.4: only Date column is sortable now; sortKey is implicit (created_at)
  const [sortDir, setSortDir] = useState('desc');

  // P4.3: country filter (single-select dropdown)
  const [countryFilter, setCountryFilter] = useState('all');

  // P4.4: status filter (single-select dropdown)
  const [statusFilter, setStatusFilter] = useState('all');
  
  // Delete state
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [searchToDelete, setSearchToDelete] = useState(null);
  // P4.6: bulk delete state
  const [bulkDeleteDialogOpen, setBulkDeleteDialogOpen] = useState(false);
  const [bulkDeleting, setBulkDeleting] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  // Fetch searches function (reusable)
  const fetchSearches = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/reports/searches`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSearches(response.data);
    } catch (error) {
      toast.error('Failed to load search history');
    } finally {
      setLoading(false);
    }
  }, [token]);

  // Load searches on mount and whenever token changes
  useEffect(() => {
    fetchSearches();
  }, [fetchSearches]);

  // Calculate combined summary for selected searches
  const selectedSummary = useMemo(() => {
    const selected = searches.filter(s => selectedSearchIds.has(s.id));
    return {
      count: selected.length,
      totalLeads: selected.reduce((sum, s) => sum + s.total_leads, 0),
      approvedLeads: selected.reduce((sum, s) => sum + s.approved_count, 0),
      rejectedLeads: selected.reduce((sum, s) => sum + s.rejected_count, 0),
      pendingLeads: selected.reduce((sum, s) => sum + s.pending_count, 0),
    };
  }, [searches, selectedSearchIds]);

  // P4.3: unique countries available across all reports (for the dropdown)
  const availableCountries = useMemo(() => {
    const set = new Set();
    searches.forEach(s => {
      (s.parameters?.countries || []).forEach(c => {
        if (c) set.add(c);
      });
    });
    return Array.from(set).sort();
  }, [searches]);

  // P4.2 + P4.3 + P4.4: filtered + sorted searches (purely client-side, no data mutation)
  const visibleSearches = useMemo(() => {
    const getName = (s) => (s.parameters?.categories || []).join(', ').toLowerCase();
    const getCountries = (s) => (s.parameters?.countries || []).join(', ').toLowerCase();
    const getCategories = (s) => (s.parameters?.categories || []).join(', ').toLowerCase();

    // Filter — text search
    const q = searchQuery.trim().toLowerCase();
    let filtered = !q
      ? searches
      : searches.filter(s =>
          getName(s).includes(q) ||
          getCountries(s).includes(q) ||
          getCategories(s).includes(q)
        );

    // P4.3: country filter (combined with text search)
    if (countryFilter !== 'all') {
      filtered = filtered.filter(s =>
        (s.parameters?.countries || []).includes(countryFilter)
      );
    }

    // P4.4: status filter (combined via AND)
    if (statusFilter !== 'all') {
      filtered = filtered.filter(s => {
        switch (statusFilter) {
          case 'with_approved': return (s.approved_count || 0) > 0;
          case 'with_pending':  return (s.pending_count || 0) > 0;
          case 'completed':     return (s.pending_count || 0) === 0;
          default: return true;
        }
      });
    }

    // P4.4: only Date is sortable now — sort by created_at
    const dir = sortDir === 'asc' ? 1 : -1;
    return [...filtered].sort((a, b) => {
      const av = new Date(a.created_at).getTime();
      const bv = new Date(b.created_at).getTime();
      if (av < bv) return -1 * dir;
      if (av > bv) return 1 * dir;
      return 0;
    });
  }, [searches, searchQuery, countryFilter, statusFilter, sortDir]);

  // P4.4: only Date is sortable — toggle asc/desc on click
  const handleDateSort = () => {
    setSortDir(prev => (prev === 'asc' ? 'desc' : 'asc'));
  };

  const DateSortIcon = () => (
    sortDir === 'asc'
      ? <ArrowUp className="h-3 w-3 ml-1 inline-block text-blue-400" />
      : <ArrowDown className="h-3 w-3 ml-1 inline-block text-blue-400" />
  );

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatShortDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  // P4.2: short date + time for the secondary label on each row
  const formatDateTime = (dateString) => {
    const d = new Date(dateString);
    const date = d.toLocaleDateString('en-US', {
      month: 'short', day: 'numeric', year: 'numeric'
    });
    const time = d.toLocaleTimeString('en-US', {
      hour: '2-digit', minute: '2-digit', hour12: false
    });
    return `${date}, ${time}`;
  };

  const getSearchName = (search) => {
    const categories = search.parameters?.categories || [];
    const countries = search.parameters?.countries || [];
    if (categories.length > 0) {
      return categories.slice(0, 2).join(', ') + (categories.length > 2 ? '...' : '');
    }
    if (countries.length > 0) {
      return countries.slice(0, 2).join(', ') + (countries.length > 2 ? '...' : '');
    }
    return 'Search';
  };

  const getCountryDisplay = (search) => {
    const countries = search.parameters?.countries || [];
    if (countries.length === 0) return '-';
    if (countries.length === 1) return countries[0];
    return `${countries[0]} +${countries.length - 1}`;
  };

  const getCategoryDisplay = (search) => {
    const categories = search.parameters?.categories || [];
    if (categories.length === 0) return '-';
    if (categories.length === 1) return categories[0];
    return `${categories[0]} +${categories.length - 1}`;
  };

  const handleGoToReview = (searchId) => {
    navigate(`/dashboard?search=${searchId}`);
  };

  const handleGoToExport = (searchId) => {
    navigate(`/export?search=${searchId}`);
  };

  // Selection handlers
  const handleSelectSearch = (searchId, checked) => {
    setSelectedSearchIds(prev => {
      const newSet = new Set(prev);
      if (checked) {
        newSet.add(searchId);
      } else {
        newSet.delete(searchId);
      }
      return newSet;
    });
  };

  const handleSelectAll = (checked) => {
    if (checked) {
      // P4.2: when filtered, "select all" only adds visible rows (preserves prior selections)
      setSelectedSearchIds(prev => {
        const newSet = new Set(prev);
        visibleSearches.forEach(s => newSet.add(s.id));
        return newSet;
      });
    } else {
      // Unselect all visible (preserve any selections outside the current filter)
      setSelectedSearchIds(prev => {
        const newSet = new Set(prev);
        visibleSearches.forEach(s => newSet.delete(s.id));
        return newSet;
      });
    }
  };

  // Combined Report: the selected reports (one or more) exported as ONE file through
  // /exports/multi. The backend orders the rows by each lead's market country and copies
  // nothing — the original reports stay exactly as they are.
  const handleMultiExport = async (format) => {
    if (selectedSearchIds.size === 0 || selectedSummary.approvedLeads === 0) return;
    
    setExporting(true);
    try {
      const response = await axios.post(
        `${API_URL}/exports/multi`,
        { search_ids: Array.from(selectedSearchIds), format },
        {
          headers: { Authorization: `Bearer ${token}` },
          responseType: 'blob'
        }
      );
      
      // Get filename from response header or generate one. Cross-origin the browser cannot
      // read Content-Disposition (the API does not expose it), so in production this fallback
      // IS the name the user gets.
      const contentDisposition = response.headers['content-disposition'];
      let filename = `ciaolead_combined_report.${format}`;
      if (contentDisposition) {
        const match = contentDisposition.match(/filename=(.+)/);
        if (match) filename = match[1];
      }
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      // "selected", not "exported": the backend drops a duplicate domain, so the file can hold
      // fewer rows than the selection's approved total.
      toast.success(
        `Combined Report exported from ${selectedSearchIds.size} report${selectedSearchIds.size !== 1 ? 's' : ''}`,
        { description: `${selectedSummary.approvedLeads} approved leads selected, grouped by market country.` }
      );
    } catch (error) {
      const message = error.response?.data?.detail || 'Export failed';
      toast.error(message);
    } finally {
      setExporting(false);
    }
  };

  // Delete handlers
  const handleDeleteClick = (search) => {
    setSearchToDelete(search);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!searchToDelete || isDeleting) return;
    
    setIsDeleting(true);
    try {
      await axios.delete(`${API_URL}/searches/${searchToDelete.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('Search deleted');
      setDeleteDialogOpen(false);
      setSearchToDelete(null);
      
      // Remove from selection if selected
      setSelectedSearchIds(prev => {
        const newSet = new Set(prev);
        newSet.delete(searchToDelete.id);
        return newSet;
      });
      
      // If we're viewing the deleted search's details, go back to list
      if (selectedSearch?.id === searchToDelete.id) {
        setSelectedSearch(null);
      }
      
      // Refresh the list
      await fetchSearches();
    } catch (error) {
      if (error.response?.status === 409) {
        toast.error('Running searches cannot be deleted');
      } else if (error.response?.status === 404) {
        toast.error('Search not found');
      } else {
        toast.error('Failed to delete search');
      }
    } finally {
      setIsDeleting(false);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
    setSearchToDelete(null);
  };

  // P4.6: bulk delete handler
  const handleBulkDeleteConfirm = async () => {
    if (selectedSearchIds.size === 0 || bulkDeleting) return;

    setBulkDeleting(true);
    const ids = Array.from(selectedSearchIds);
    try {
      const response = await axios.post(
        `${API_URL}/searches/bulk-delete`,
        { search_ids: ids },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      const { deleted_search_ids = [], not_found_search_ids = [] } = response.data || {};

      // If we're viewing one of the deleted searches' details, go back to list
      if (selectedSearch && deleted_search_ids.includes(selectedSearch.id)) {
        setSelectedSearch(null);
      }

      // Clear selection
      setSelectedSearchIds(new Set());
      setBulkDeleteDialogOpen(false);

      const deletedCount = deleted_search_ids.length;
      if (deletedCount > 0) {
        toast.success(`Deleted ${deletedCount} search${deletedCount !== 1 ? 'es' : ''}`);
      }
      if (not_found_search_ids.length > 0) {
        toast.error(
          `${not_found_search_ids.length} search${not_found_search_ids.length !== 1 ? 'es' : ''} could not be deleted (not found or running)`
        );
      }

      await fetchSearches();
    } catch (error) {
      console.error('Bulk delete failed:', error);
      if (error.response?.status === 400) {
        toast.error(error.response?.data?.detail || 'No searches selected');
      } else {
        toast.error('Failed to delete searches');
      }
    } finally {
      setBulkDeleting(false);
    }
  };

  // Search Detail View
  if (selectedSearch) {
    return (
      <div className="space-y-6" data-testid="reports-detail-page">
        {/* Header */}
        <div className="flex items-center gap-4">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setSelectedSearch(null)}
            className="h-9 px-3"
            data-testid="back-to-list-btn"
          >
            <ArrowLeft className="h-4 w-4 mr-1" />
            Back to History
          </Button>
        </div>

        <Card className="world-card">
          <CardHeader>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="h-10 w-10 rounded-lg flex items-center justify-center world-icon-box">
                  <FileText className="h-5 w-5 text-blue-400" />
                </div>
                <div>
                  <CardTitle className="text-xl text-white">Search Details</CardTitle>
                  <CardDescription className="text-slate-400">
                    {formatDate(selectedSearch.created_at)}
                  </CardDescription>
                </div>
              </div>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  onClick={() => handleGoToReview(selectedSearch.id)}
                  data-testid="detail-review-btn"
                >
                  <Search className="h-4 w-4 mr-2" />
                  Go to Review
                </Button>
                <Button
                  onClick={() => handleGoToExport(selectedSearch.id)}
                  className="bg-blue-600 hover:bg-blue-700"
                  disabled={selectedSearch.approved_count === 0}
                  data-testid="detail-export-btn"
                >
                  <Download className="h-4 w-4 mr-2" />
                  Go to Export
                </Button>
                <Button
                  variant="outline"
                  onClick={() => handleDeleteClick(selectedSearch)}
                  className="text-red-400 border-red-500/40 hover:bg-red-500/15 hover:border-red-500/50"
                  data-testid="detail-delete-btn"
                >
                  <Trash2 className="h-4 w-4 mr-2" />
                  Delete
                </Button>
              </div>
            </div>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Search Parameters */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-sm text-slate-400 mb-1">Categories</p>
                <p className="font-medium text-white">
                  {selectedSearch.parameters?.categories?.join(', ') || 'None'}
                </p>
              </div>
              <div>
                <p className="text-sm text-slate-400 mb-1">Countries</p>
                <p className="font-medium text-white">
                  {selectedSearch.parameters?.countries?.join(', ') || 'None'}
                </p>
              </div>
            </div>

            {/* Metrics */}
            <div className="grid grid-cols-4 gap-4">
              <div className="bg-white/5 rounded-lg p-4">
                <p className="text-sm text-slate-400 mb-1">Total Leads</p>
                <p className="text-2xl font-bold text-white">{selectedSearch.total_leads}</p>
              </div>
              <div className="bg-green-500/10 rounded-lg p-4">
                <div className="flex items-center gap-2 mb-1">
                  <CheckCircle2 className="h-4 w-4 text-green-400" />
                  <p className="text-sm text-green-400">Approved</p>
                </div>
                <p className="text-2xl font-bold text-green-300">{selectedSearch.approved_count}</p>
              </div>
              <div className="bg-red-500/10 rounded-lg p-4">
                <div className="flex items-center gap-2 mb-1">
                  <XCircle className="h-4 w-4 text-red-400" />
                  <p className="text-sm text-red-400">Rejected</p>
                </div>
                <p className="text-2xl font-bold text-red-300">{selectedSearch.rejected_count}</p>
              </div>
              <div className="bg-amber-500/10 rounded-lg p-4">
                <div className="flex items-center gap-2 mb-1">
                  <Clock className="h-4 w-4 text-amber-400" />
                  <p className="text-sm text-amber-400">Pending</p>
                </div>
                <p className="text-2xl font-bold text-amber-300">{selectedSearch.pending_count}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        {/* Delete Confirmation Dialog */}
        <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
          <AlertDialogContent data-testid="delete-search-dialog">
            <AlertDialogHeader>
              <AlertDialogTitle>Delete search?</AlertDialogTitle>
              <AlertDialogDescription className="space-y-2">
                <p>This will permanently delete this search and all related data.</p>
                <p className="text-slate-300 font-medium">Learning will NOT be affected.</p>
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel 
                onClick={handleDeleteCancel}
                disabled={isDeleting}
                data-testid="delete-cancel-btn"
              >
                Cancel
              </AlertDialogCancel>
              <AlertDialogAction
                onClick={handleDeleteConfirm}
                disabled={isDeleting}
                className="bg-red-600 hover:bg-red-700 focus:ring-red-600"
                data-testid="delete-confirm-btn"
              >
                {isDeleting ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Deleting...
                  </>
                ) : (
                  'Delete'
                )}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    );
  }

  // Main Reports Dashboard View
  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 className="h-8 w-8 animate-spin text-blue-400" />
      </div>
    );
  }

  return (
    <div className="space-y-6 relative" data-testid="reports-page">
      <WorldGlobe size={320} className="hidden lg:block opacity-70" style={{ top: '-55px', right: '-30px' }} />
      <Card className="world-card">
        <CardHeader>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-lg flex items-center justify-center world-icon-box">
                <History className="h-5 w-5 text-blue-400" />
              </div>
              <div>
                <CardTitle className="text-xl text-white">Search History</CardTitle>
                <CardDescription className="text-slate-400">
                  {searches.length} search{searches.length !== 1 ? 'es' : ''} · Select one or more to export a Combined Report
                </CardDescription>
              </div>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {searches.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16 text-center">
              <div className="h-16 w-16 rounded-full bg-white/10 flex items-center justify-center mb-4">
                <History className="h-8 w-8 text-slate-400" />
              </div>
              <h3 className="text-lg font-medium text-white mb-2">
                No searches yet
              </h3>
              <p className="text-slate-400 max-w-md mb-4">
                Your search history will appear here once you perform your first search.
              </p>
              <Button onClick={() => navigate('/dashboard')} className="bg-blue-600 hover:bg-blue-700">
                <Search className="h-4 w-4 mr-2" />
                Start Searching
              </Button>
            </div>
          ) : (
            <div className="space-y-4">
              {/* Multi-Select Actions Bar */}
              {selectedSearchIds.size > 0 && (
                <div 
                  className="bg-blue-500/10 border border-blue-500/40 rounded-lg p-4"
                  data-testid="multi-select-bar"
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-6">
                      <span className="text-sm font-medium text-blue-300">
                        {selectedSearchIds.size} search{selectedSearchIds.size !== 1 ? 'es' : ''} selected
                      </span>
                      <div className="flex items-center gap-4 text-sm">
                        <span className="text-slate-300">
                          <span className="font-medium">{selectedSummary.totalLeads}</span> total
                        </span>
                        <span className="text-green-400">
                          <span className="font-medium">{selectedSummary.approvedLeads}</span> approved
                        </span>
                        <span className="text-red-400">
                          <span className="font-medium">{selectedSummary.rejectedLeads}</span> rejected
                        </span>
                        <span className="text-amber-400">
                          <span className="font-medium">{selectedSummary.pendingLeads}</span> pending
                        </span>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setSelectedSearchIds(new Set())}
                        className="text-blue-400 hover:text-blue-300 h-8"
                      >
                        Clear
                      </Button>
                      <Button
                        size="sm"
                        onClick={() => handleMultiExport('csv')}
                        disabled={exporting || selectedSummary.approvedLeads === 0}
                        className="bg-blue-600 hover:bg-blue-700 h-8"
                        data-testid="multi-export-csv-btn"
                        title="One CSV with the approved leads of every selected report, grouped by market country"
                      >
                        {exporting ? (
                          <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                        ) : (
                          <Download className="h-4 w-4 mr-1" />
                        )}
                        Combined Report (CSV)
                      </Button>
                      <Button
                        size="sm"
                        onClick={() => handleMultiExport('xlsx')}
                        disabled={exporting || selectedSummary.approvedLeads === 0}
                        className="bg-blue-600 hover:bg-blue-700 h-8"
                        data-testid="multi-export-xlsx-btn"
                        title="One Excel file with the approved leads of every selected report, grouped by market country"
                      >
                        {exporting ? (
                          <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                        ) : (
                          <FileSpreadsheet className="h-4 w-4 mr-1" />
                        )}
                        Combined Report (Excel)
                      </Button>
                      {/* P4.6: Bulk delete */}
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => setBulkDeleteDialogOpen(true)}
                        disabled={bulkDeleting}
                        className="border-red-500/40 text-red-400 hover:bg-red-500/15 h-8"
                        data-testid="multi-delete-btn"
                      >
                        {bulkDeleting ? (
                          <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                        ) : (
                          <Trash2 className="h-4 w-4 mr-1" />
                        )}
                        Delete selected ({selectedSearchIds.size})
                      </Button>
                    </div>
                  </div>
                </div>
              )}

              {/* P4.2 + P4.3: Filter row — text search + country dropdown */}
              <div className="flex flex-wrap items-center gap-3">
                <div className="relative max-w-sm flex-1 min-w-[220px]">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400 pointer-events-none" />
                  <Input
                    type="text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder="Search reports..."
                    className="pl-9 pr-9 h-9"
                    data-testid="reports-filter-input"
                  />
                  {searchQuery && (
                    <button
                      type="button"
                      onClick={() => setSearchQuery('')}
                      className="absolute right-2 top-1/2 -translate-y-1/2 h-6 w-6 rounded flex items-center justify-center text-slate-400 hover:text-white hover:bg-white/10"
                      aria-label="Clear filter"
                      data-testid="reports-filter-clear"
                    >
                      <X className="h-3.5 w-3.5" />
                    </button>
                  )}
                </div>

                <Select value={countryFilter} onValueChange={setCountryFilter}>
                  <SelectTrigger
                    className="w-[200px] h-9"
                    data-testid="reports-country-filter"
                    aria-label="Filter by country"
                  >
                    <SelectValue placeholder="All countries" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all" data-testid="country-option-all">All countries</SelectItem>
                    {availableCountries.map((c) => (
                      <SelectItem key={c} value={c} data-testid={`country-option-${c}`}>
                        {c}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>

                <Select value={statusFilter} onValueChange={setStatusFilter}>
                  <SelectTrigger
                    className="w-[220px] h-9"
                    data-testid="reports-status-filter"
                    aria-label="Filter by status"
                  >
                    <SelectValue placeholder="Status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all" data-testid="status-option-all">All</SelectItem>
                    <SelectItem value="with_approved" data-testid="status-option-with-approved">With approved leads</SelectItem>
                    <SelectItem value="with_pending" data-testid="status-option-with-pending">With pending leads</SelectItem>
                    <SelectItem value="completed" data-testid="status-option-completed">Completed</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Search Table */}
              <div className="border border-white/10 rounded-lg overflow-hidden">
                <table className="w-full" data-testid="searches-table">
                  <thead className="bg-white/5 border-b border-white/10">
                    <tr>
                      <th className="w-12 px-4 py-3">
                        <Checkbox
                          checked={
                            visibleSearches.length > 0 &&
                            visibleSearches.every(s => selectedSearchIds.has(s.id))
                          }
                          onCheckedChange={handleSelectAll}
                          data-testid="select-all-searches"
                        />
                      </th>
                      <th
                        className="text-left px-4 py-3 text-xs font-medium text-slate-400 uppercase tracking-wider"
                      >
                        Search
                      </th>
                      <th
                        className="text-left px-4 py-3 text-xs font-medium text-slate-400 uppercase tracking-wider"
                      >
                        Country
                      </th>
                      <th
                        className="text-left px-4 py-3 text-xs font-medium text-slate-400 uppercase tracking-wider"
                      >
                        Category
                      </th>
                      <th
                        onClick={handleDateSort}
                        className="text-left px-4 py-3 text-xs font-medium text-slate-400 uppercase tracking-wider cursor-pointer select-none hover:text-white"
                        data-testid="sort-header-date"
                      >
                        Date<DateSortIcon />
                      </th>
                      <th
                        className="text-center px-4 py-3 text-xs font-medium text-slate-400 uppercase tracking-wider"
                      >
                        Total
                      </th>
                      <th
                        className="text-center px-4 py-3 text-xs font-medium text-slate-400 uppercase tracking-wider"
                      >
                        Approved
                      </th>
                      <th
                        className="text-center px-4 py-3 text-xs font-medium text-slate-400 uppercase tracking-wider"
                      >
                        Rejected
                      </th>
                      <th
                        className="text-center px-4 py-3 text-xs font-medium text-slate-400 uppercase tracking-wider"
                      >
                        Pending
                      </th>
                      <th className="text-right px-4 py-3 text-xs font-medium text-slate-400 uppercase tracking-wider">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-white/5">
                    {visibleSearches.length === 0 ? (
                      <tr>
                        <td colSpan="10" className="px-4 py-12 text-center" data-testid="reports-no-matches">
                          <div className="flex flex-col items-center gap-2">
                            <Search className="h-6 w-6 text-slate-300" />
                            <p className="text-sm text-slate-400" data-testid="reports-no-matches-message">
                              No searches match the current filters
                            </p>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => { setSearchQuery(''); setCountryFilter('all'); setStatusFilter('all'); }}
                              className="text-blue-400 hover:text-blue-300 h-7"
                              data-testid="reports-clear-filters"
                            >
                              Clear filters
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ) : visibleSearches.map((search) => (
                      <tr 
                        key={search.id} 
                        className={`hover:bg-white/5 ${selectedSearchIds.has(search.id) ? 'bg-blue-500/10' : ''}`}
                        data-testid={`search-row-${search.id}`}
                      >
                        <td className="px-4 py-3">
                          <Checkbox
                            checked={selectedSearchIds.has(search.id)}
                            onCheckedChange={(checked) => handleSelectSearch(search.id, checked)}
                            data-testid={`select-search-${search.id}`}
                          />
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex flex-col gap-0.5">
                            <div className="flex items-center gap-2">
                              <span className="font-medium text-white text-sm">
                                {getSearchName(search)}
                              </span>
                              {search.status === 'running' && (
                                <Badge variant="outline" className="bg-amber-500/10 text-amber-300 border-amber-500/40">
                                  <Loader2 className="h-3 w-3 mr-1 animate-spin" />
                                  Running
                                </Badge>
                              )}
                              {/* P9: unviewed searches — clears once the search is opened. */}
                              {search.viewed === false && (
                                <Badge
                                  variant="outline"
                                  className="bg-blue-500/10 text-blue-300 border-blue-500/40"
                                  data-testid={`search-new-badge-${search.id}`}
                                >
                                  New
                                </Badge>
                              )}
                            </div>
                            <span className="text-xs text-slate-400" data-testid={`search-secondary-${search.id}`}>
                              {getCountryDisplay(search)} · {formatDateTime(search.created_at)}
                            </span>
                          </div>
                        </td>
                        <td className="px-4 py-3 text-sm text-slate-300">
                          {getCountryDisplay(search)}
                        </td>
                        <td className="px-4 py-3 text-sm text-slate-300">
                          {getCategoryDisplay(search)}
                        </td>
                        <td className="px-4 py-3 text-sm text-slate-400">
                          {formatShortDate(search.created_at)}
                        </td>
                        <td className="px-4 py-3 text-center">
                          <span className="text-sm font-medium text-white">{search.total_leads}</span>
                        </td>
                        <td className="px-4 py-3 text-center">
                          <span className="text-sm font-medium text-green-400">{search.approved_count}</span>
                        </td>
                        <td className="px-4 py-3 text-center">
                          <span className="text-sm font-medium text-red-400">{search.rejected_count}</span>
                        </td>
                        <td className="px-4 py-3 text-center">
                          <span className="text-sm font-medium text-amber-400">{search.pending_count}</span>
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center justify-end gap-1">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleGoToReview(search.id)}
                              className="h-8 px-2"
                              data-testid={`review-btn-${search.id}`}
                            >
                              <Search className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleGoToExport(search.id)}
                              className="h-8 px-2"
                              disabled={search.approved_count === 0}
                              data-testid={`export-btn-${search.id}`}
                            >
                              <Download className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleDeleteClick(search)}
                              className="h-8 px-2 text-slate-400 hover:text-red-600 hover:bg-red-500/15"
                              data-testid={`delete-btn-${search.id}`}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => setSelectedSearch(search)}
                              className="h-8 px-2"
                              data-testid={`details-btn-${search.id}`}
                            >
                              <ChevronRight className="h-4 w-4" />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
      
      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent data-testid="delete-search-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete search?</AlertDialogTitle>
            <AlertDialogDescription className="space-y-2">
              <p>This will permanently delete this search and all related data.</p>
              <p className="text-slate-300 font-medium">Learning will NOT be affected.</p>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel 
              onClick={handleDeleteCancel}
              disabled={isDeleting}
              data-testid="delete-cancel-btn"
            >
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteConfirm}
              disabled={isDeleting}
              className="bg-red-600 hover:bg-red-700 focus:ring-red-600"
              data-testid="delete-confirm-btn"
            >
              {isDeleting ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Deleting...
                </>
              ) : (
                'Delete'
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* P4.6: Bulk Delete Confirmation Dialog */}
      <AlertDialog
        open={bulkDeleteDialogOpen}
        onOpenChange={(open) => {
          // Don't allow accidental dismissal while deleting
          if (bulkDeleting) return;
          setBulkDeleteDialogOpen(open);
        }}
      >
        <AlertDialogContent data-testid="bulk-delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>
              Delete {selectedSearchIds.size} search{selectedSearchIds.size !== 1 ? 'es' : ''}?
            </AlertDialogTitle>
            <AlertDialogDescription className="space-y-2">
              <p className="text-red-400 font-medium">
                This action is irreversible.
              </p>
              <p>
                {selectedSearchIds.size} search{selectedSearchIds.size !== 1 ? 'es' : ''} and all related data
                (leads, decisions, enrichments) will be permanently deleted.
              </p>
              <p className="text-slate-300 font-medium">Learning will NOT be affected.</p>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel
              disabled={bulkDeleting}
              data-testid="bulk-delete-cancel-btn"
            >
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleBulkDeleteConfirm}
              disabled={bulkDeleting}
              className="bg-red-600 hover:bg-red-700 focus:ring-red-600"
              data-testid="bulk-delete-confirm-btn"
            >
              {bulkDeleting ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Deleting...
                </>
              ) : (
                `Delete ${selectedSearchIds.size} search${selectedSearchIds.size !== 1 ? 'es' : ''}`
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default Reports;
